import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { Notificacao } from '@/ui/Notificacao';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  FileOutput,
  Activity,
  ArrowRight,
  CircleCheck,
} from 'lucide-react';

import { useConfirmarPagamentoStatusSof, useStatusSof } from '../hooks/useSofExtras';
import type { StatusSofLinha } from '../types';
import { EstagioSofBadge } from './EstagioSofBadge';
import { SofHeader } from './SofHeader';
import { SofTabsNav } from './SofTabsNav';

/** FIN-031.2: painel read-only do ciclo empenho → liquidação → pagamento (a
 * conciliação bancária de fato é responsabilidade da SOF, fora deste sistema). */
export function SofStatusPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useStatusSof();
  const confirmarPagamento = useConfirmarPagamentoStatusSof();

  const [pagarAlvo, definirPagarAlvo] = useState<StatusSofLinha | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);

  const linhas = data ?? [];

  const stats: StatItem[] = useMemo(() => {
    const base = data ?? [];
    const pagas = base.filter((l) => l.estagioSof === 'PAGA');
    const emTransito = base.filter(
      (l) => l.estagioSof === 'EXPORTADA_SOF' || l.estagioSof === 'CONFIRMADO_SOF',
    );
    const rejeitadas = base.filter((l) => l.estagioSof === 'REJEITADA');
    const valorPago = pagas.reduce((s, l) => s + parseFloat(l.valor), 0);
    return [
      {
        title: 'Medições monitoradas',
        value: base.length,
        description: 'No período',
        icon: Activity,
      },
      {
        title: 'Pagas',
        value: pagas.length,
        description: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
          valorPago,
        ),
        icon: CircleCheck,
      },
      {
        title: 'No SOF (em trânsito)',
        value: emTransito.length,
        description: 'Enviadas / confirmadas',
        icon: FileOutput,
      },
      {
        title: 'Rejeitadas',
        value: rejeitadas.length,
        description: rejeitadas.length > 0 ? 'Requerem reenvio' : 'Nenhuma',
        icon: AlertTriangle,
      },
    ];
  }, [data]);

  async function confirmarPagamentoAlvo() {
    if (!pagarAlvo) return;
    try {
      await confirmarPagamento.mutateAsync(pagarAlvo.medicaoId);
      definirAviso(`Pagamento de ${pagarAlvo.credenciadoNome} confirmado.`);
      definirPagarAlvo(null);
    } catch {
      definirAviso('Não foi possível confirmar o pagamento. Tente novamente.');
    }
  }

  const colunas: DataTableColumn<StatusSofLinha>[] = [
    {
      header: 'Mês',
      sortKey: 'mesReferencia',
      cell: (linha) =>
        new Date(`${linha.mesReferencia}T00:00:00`).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    { header: 'Credenciado', accessorKey: 'credenciadoNome', sortKey: 'credenciadoNome' },
    {
      header: 'Valor',
      cell: (linha) => <MoneyDisplay value={linha.valor} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      sortKey: (linha) => parseFloat(linha.valor),
    },
    {
      header: 'Status SOF',
      sortKey: 'estagioSof',
      cell: (linha) => <EstagioSofBadge estagio={linha.estagioSof} />,
    },
    {
      header: 'Ações',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (linha) => (
        <div className="flex justify-end gap-1">
          {linha.pagavel && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                definirPagarAlvo(linha);
              }}
            >
              <Banknote className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Confirmar pagamento
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/operacao/financeiro/sof/conciliacao?busca=${encodeURIComponent(linha.credenciadoNome)}`,
              );
            }}
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Conciliar
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      {aviso && <Notificacao tom="sucesso" mensagem={aviso} aoFechar={() => definirAviso(null)} />}

      <StatsGrid stats={stats} columns="grid-cols-2 md:grid-cols-4" />

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <p className="text-base font-semibold">Acompanhamento por medição</p>
              <p className="text-sm text-muted-foreground">
                Estágio mais avançado conhecido. Clique na linha para abrir o detalhe da medição.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate('/operacao/financeiro/sof/relatorios')}
            >
              <FileOutput className="h-4 w-4" aria-hidden="true" />
              Exportações SOF
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={colunas}
              data={linhas}
              isLoading={isLoading}
              onRowClick={(linha) => navigate(`/operacao/financeiro/medicoes/${linha.medicaoId}`)}
              emptyTitle="Sem medições recentes"
              emptyDescription="Não há medições monitoradas no período."
              ariaLabel="Medições monitoradas no SOF"
            />
          </CardContent>
        </Card>
      )}

      <ResponsiveModal
        open={!!pagarAlvo}
        onOpenChange={(aberto) => !aberto && definirPagarAlvo(null)}
        title="Confirmar pagamento"
        description={
          pagarAlvo
            ? `${pagarAlvo.credenciadoNome} — ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(pagarAlvo.valor))}`
            : undefined
        }
      >
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => definirPagarAlvo(null)}
            disabled={confirmarPagamento.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => void confirmarPagamentoAlvo()}
            disabled={confirmarPagamento.isPending}
          >
            {confirmarPagamento.isPending ? 'Confirmando…' : 'Confirmar pagamento'}
          </Button>
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default SofStatusPage;
